import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill } from "@/components/ui";

interface DoubleHopItem {
  id: number;
  name: string;
  bridge_host: string;
  bridge_port: number;
  exit_host: string;
  exit_port: number;
  is_active: boolean;
  max_users: number;
  active_users: number;
  health_reachable: boolean | null;
  health_active: boolean | null;
  health_kind: string | null;
  test_link: string;
  created_at: string;
}

interface DoubleHopsResponse {
  items: DoubleHopItem[];
}

export default function DoubleHops() {
  const q = useQuery<DoubleHopsResponse>({
    queryKey: ["double-hops"],
    queryFn: () => api("/api/admin/double-hops"),
    refetchInterval: 30000,
  });

  const items = q.data?.items ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Дабл-хоп"
        right={
          <div className="text-zinc-400 text-sm">всего: {items.length}</div>
        }
      />

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}

      {!q.isLoading && items.length === 0 && (
        <div className="text-zinc-500">
          Premium-туннелей пока нет. Дабл-хоп = пара серверов (bridge в whitelist-сети
          + exit за границей), которая показывается юзеру как один сервер при покупке
          NoLags Premium. Добавление через прямой INSERT в БД (UI пока не сделан).
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {items.map((it) => (
          <DoubleHopRow key={it.id} item={it} />
        ))}
      </div>
    </div>
  );
}

function DoubleHopRow({ item }: { item: DoubleHopItem }) {
  const ok = item.health_reachable && item.health_active;
  const tone =
    item.health_reachable === null
      ? "neutral"
      : ok
        ? "success"
        : "danger";
  const label =
    item.health_reachable === null
      ? "проверка…"
      : !item.health_reachable
        ? "exit недоступен"
        : item.health_active
          ? "ok"
          : "сервис лежит";

  const fillPct = Math.min(
    100,
    Math.round((item.active_users / Math.max(1, item.max_users)) * 100),
  );

  return (
    <Card
      title={
        <span className="flex items-center gap-3 flex-wrap">
          <span>{item.name}</span>
          {!item.is_active && <Pill tone="danger">выключен</Pill>}
          <Pill tone={tone as "success" | "danger" | "neutral"}>{label}</Pill>
        </span>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Цепочка
          </div>
          <div className="font-mono text-sm leading-6">
            <span className="text-emerald-300">{item.bridge_host}</span>
            <span className="text-zinc-500">:{item.bridge_port}</span>
            <span className="text-zinc-600 mx-2">→</span>
            <span className="text-sky-300">{item.exit_host}</span>
            <span className="text-zinc-500">:{item.exit_port}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            bridge (RU whitelist) → exit (mtproto.zig)
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Юзеров
          </div>
          <div className="text-3xl font-semibold">
            <span className="text-emerald-300">{item.active_users}</span>
            <span className="text-zinc-500">/{item.max_users}</span>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded mt-2 overflow-hidden">
            <div
              className="h-full bg-emerald-400/70"
              style={{ width: `${fillPct}%` }}
            />
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Тест-ссылка
          </div>
          <button
            type="button"
            className="text-xs font-mono text-brand hover:underline truncate w-full text-left"
            title="Скопировать"
            onClick={() => {
              navigator.clipboard.writeText(item.test_link).catch(() => {});
            }}
          >
            {item.test_link.slice(0, 50)}…
          </button>
          <div className="text-xs text-zinc-500 mt-1">кликни — скопируется</div>
        </div>
        <div className="md:text-right">
          <Link
            to={`/double-hops/${item.id}`}
            className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border bg-brand/15 text-brand border-brand/40 hover:bg-brand hover:text-brand-fg hover:border-brand transition-colors"
          >
            Открыть →
          </Link>
        </div>
      </div>
    </Card>
  );
}
